import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import { geoJsonToArcGis, MapBounds } from '../helpers/project-map-inspection';
import { LiveDrawFilter, LiveFeatureDrawController, symbolGeometry } from '../helpers/live-published-features';
import type { ProjectMapFeatureResponse, ProjectMapSymbol, PublishedMapLayer } from './project-map-manifest.service';

export interface LivePublishedFeatureOverlayOptions {
  view: __esri.MapView;
  published: PublishedMapLayer;
  catalogRevision: number;
  /** The layer's row group (shown/hidden by the layers control): nothing is asked while it is hidden. */
  host: __esri.Layer;
  /** The snapshot geometry drawing (GeoJSON / raster geometry tiles, not labels), hidden while live features are drawn. */
  snapshot: __esri.Layer[];
  fetch(bbox: MapBounds, limit: number, signal: AbortSignal, cursor?: string | null): Promise<ProjectMapFeatureResponse>;
  /** Picker sessions (feature_source `where`): draw only matching features and never the unfiltered snapshot. */
  filter?: LiveDrawFilter | null;
  symbol(geometry: ProjectMapSymbol['geometry'], properties: Record<string, unknown>, featureId: string): __esri.Symbol;
}

/**
 * ArcGIS side of live-first drawing (see helpers/live-published-features.ts): a graphics layer with the live
 * features of the current view, in place of the snapshot geometry while the live answer holds. Added to the
 * published layer's content group, so it is destroyed with it; `destroy()` stops the watchers and requests.
 */
export class LivePublishedFeatureOverlay {
  readonly layer: GraphicsLayer;
  private readonly controller: LiveFeatureDrawController;
  private handles: __esri.Handle[] = [];
  /** The features drawn last, so a style change (e.g. statuses arriving) can redraw without a fetch. */
  private drawn: ProjectMapFeatureResponse['features'] | null = null;

  constructor(private readonly options: LivePublishedFeatureOverlayOptions) {
    this.layer = new GraphicsLayer({ id: `published-map-${options.published.id}-live`, title: options.published.name, listMode: 'hide' });
    this.controller = new LiveFeatureDrawController({
      view: () => this.viewState(),
      fetch: options.fetch,
      drawLive: features => this.drawLive(features),
      showSnapshot: () => this.showSnapshot(),
      setTimer: (callback, ms) => setTimeout(callback, ms),
      clearTimer: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
      hasSnapshot: () => options.snapshot.some(layer => !layer.destroyed),
    }, options.published, options.catalogRevision, options.filter || null);
    // Filtered: the unfiltered snapshot never shows, not even before the first answer.
    if (options.filter) options.snapshot.forEach(layer => { layer.visible = false; });
  }

  get mode(): 'snapshot' | 'live' { return this.controller.mode; }

  start(): void {
    const { view, host } = this.options;
    this.handles = [
      reactiveUtils.when(() => view.stationary, () => this.controller.schedule(), { initial: true }),
      reactiveUtils.watch(() => host.visible, visible => { if (visible) this.controller.schedule(); }),
    ];
  }

  /** Redraws the current features with fresh symbols (the symbol callback's inputs changed). */
  restyle(): void {
    if (this.drawn && this.controller.mode === 'live') this.drawLive(this.drawn);
  }

  destroy(): void {
    this.controller.stop();
    this.handles.forEach(handle => handle.remove());
    this.handles = [];
  }

  private viewState(): { extent: MapBounds | null; zoom: number; visible: boolean } {
    const { view, host } = this.options;
    const visible = !!host.visible && !this.layer.destroyed;
    let extent = view?.extent;
    if (extent && view.spatialReference?.isWebMercator) extent = webMercatorUtils.webMercatorToGeographic(extent) as __esri.Extent;
    else if (extent && !view.spatialReference?.isWGS84) extent = null;
    const bounds: MapBounds | null = extent ? [extent.xmin, extent.ymin, extent.xmax, extent.ymax] : null;
    return { extent: bounds, zoom: view?.zoom, visible };
  }

  /** All or nothing: one feature the map cannot draw keeps the snapshot drawing. */
  private drawLive(features: ProjectMapFeatureResponse['features']): boolean {
    if (this.layer.destroyed) return false;
    const graphics: Graphic[] = [];
    for (const feature of features) {
      const geometry = geoJsonToArcGis(feature?.geometry);
      const kind = symbolGeometry((feature?.geometry as { type?: string })?.type);
      if (!geometry || !kind) return false;
      graphics.push(new Graphic({
        geometry: geometry as unknown as __esri.GeometryUnion,
        symbol: this.options.symbol(kind, feature.properties || {}, feature.id) as __esri.SymbolUnion,
        attributes: { featureId: feature.id },
      }));
    }
    this.layer.removeAll();
    this.layer.addMany(graphics);
    this.drawn = features;
    this.layer.visible = true;
    this.options.snapshot.forEach(layer => { if (!layer.destroyed) layer.visible = false; });
    return true;
  }

  private showSnapshot(): void {
    if (!this.layer.destroyed) this.layer.removeAll();
    this.options.snapshot.forEach(layer => { if (!layer.destroyed) layer.visible = true; });
  }
}
